import { WidgetTemplatesKindEnum } from 'graphql-common';
import getFaviconUrl from '@lib/utils/getFaviconUrl';
import { AttachmentIntendedUse } from '@lib/enums/attachments';
import findAttachmentByIntendedUse, {
  AttachmentWithIntendedUse,
} from './findAttachmentByIntendedUse';

/**
 * Result of widget icon source resolution
 */
export interface WidgetIconSource {
  /** Type of icon source */
  type: 'attachment' | 'favicon' | 'material';
  /** URL for attachment or favicon icons */
  url?: string;
  /** Material icon name for fallback */
  icon?: string;
}

/**
 * Props for getting widget icon source
 */
export interface GetWidgetIconSourceProps {
  /** Widget kind (from template or widget) */
  kind: WidgetTemplatesKindEnum | string;
  /** Attachments array from widget/template */
  attachmentsAttached?: AttachmentWithIntendedUse[] | null;
  /** Link URL for external link widgets (for favicon) */
  link?: string | null;
  /** Material icon name override (if already known) */
  materialIcon?: string;
}

/**
 * Maps widget kind to material icon name
 * Note: WidgetsKindEnum and WidgetTemplatesKindEnum have identical string values,
 * so we only need to map the string values once
 */
export function getWidgetMaterialIcon(
  kind: WidgetTemplatesKindEnum | string,
): string {
  const iconMap: Record<string, string> = {
    ACTIVE_INTERVENTIONS: 'build',
    ANNOUNCEMENT: 'campaign',
    CLOCK: 'schedule',
    EXTERNAL_LINK: 'link',
    QUICK_CREATION: 'add',
    SECTORS_PERFORMANCE_METRICS_BY_DEADLINES_MISSED: 'clock_loader_20',
    SITES_PERFORMANCE_METRICS_BY_DEADLINES_MISSED: 'clock_loader_20',
    SITES_MAP: 'location_on',
    WEATHER_TODAY: 'wb_sunny',
    WEATHER_WEEK: 'wb_sunny',
  };

  return iconMap[kind] || 'widgets';
}

/**
 * Safely extracts URL from widget icon attachment
 * Returns null if attachment is malformed or URL is inaccessible
 */
function getAttachmentUrl(
  attachmentsAttached: AttachmentWithIntendedUse[] | null | undefined,
): string | null {
  try {
    const widgetIconAttachment = findAttachmentByIntendedUse(
      attachmentsAttached,
      AttachmentIntendedUse.WidgetIcon,
    );

    if (!widgetIconAttachment) {
      return null;
    }

    const url =
      widgetIconAttachment.urls?.small ||
      widgetIconAttachment.urls?.original ||
      null;

    // Validate URL format
    if (url && typeof url === 'string' && url.length > 0) {
      return url;
    }

    return null;
  } catch {
    // Malformed attachment data - fall back to other icon sources
    return null;
  }
}

/**
 * Safely extracts favicon URL for external link widgets
 * Returns null if link is invalid or favicon cannot be resolved
 */
function getExternalLinkFaviconUrl(
  kind: WidgetTemplatesKindEnum | string,
  link: string | null | undefined,
): string | null {
  try {
    const isExternalLink = kind === WidgetTemplatesKindEnum.ExternalLink;

    if (!isExternalLink || !link || typeof link !== 'string') {
      return null;
    }

    // getFaviconUrl already handles URL parsing errors internally
    return getFaviconUrl(link);
  } catch {
    // Invalid link format - fall back to other icon sources
    return null;
  }
}

/**
 * Determines the icon source for a widget with priority:
 * 1. Widget icon attachment (by intendedUse)
 * 2. Favicon (for external link widgets)
 * 3. Material icon fallback
 *
 * Note: All errors are handled gracefully with Material icon fallback
 */
export default function getWidgetIconSource({
  kind,
  attachmentsAttached,
  link,
  materialIcon,
}: GetWidgetIconSourceProps): WidgetIconSource {
  // Priority 1: Widget icon attachment
  const attachmentUrl = getAttachmentUrl(attachmentsAttached);
  if (attachmentUrl) {
    return { type: 'attachment', url: attachmentUrl };
  }

  // Priority 2: Favicon for external link widgets
  const faviconUrl = getExternalLinkFaviconUrl(kind, link);
  if (faviconUrl) {
    return { type: 'favicon', url: faviconUrl };
  }

  // Priority 3: Material icon fallback (always succeeds)
  const icon = materialIcon || getWidgetMaterialIcon(kind);
  return { type: 'material', icon };
}
